import { createContext, FC, useContext, useState } from 'react';

import { RecentPortfoliosActions } from '../common/Constants';
import LocalStorage from '../common/LocalStorage';
import { CookieConsentContext } from './CookieConsentContext';

export interface IRecentPortfoliosContext {
    recentPortfolioIds: string[];
    visited: (portfolioId: string, action: RecentPortfoliosActions) => void;
    getMostRecent: (count: number) => string[];
    refreshList: () => void;
}

export const RecentPortfoliosContext = createContext<IRecentPortfoliosContext>({
    recentPortfolioIds: [],
    visited: () => {},
    getMostRecent: () => [],
    refreshList: () => {},
});

export const RecentPortfoliosProvider: FC = ({ children }) => {
    const { getLocalStorage, setLocalStorage } = useContext(CookieConsentContext);
    const [ recentPortfolioIds, setRecentPortfolioIds ] = useState<string[]>([]);

    /**
     * Add, moves, or removes the given portfolio id from the recent portfolio list
     * if action is ADD, it adds the portfolio to the beginning of the list, or moves
     * it up if it already exists
     * if action is REMOVE, it removes the id from the list
     * @param portfolioId given id
     * @param action ADD or REMOVE
     * @returns 
     */
    const visited = (portfolioId: string, action: RecentPortfoliosActions) => {
        if (!portfolioId) {
            return;
        }
    
        // call into localStorageService and retrieve the list
        let recentPortfolioIds: string[] = LocalStorage.getRecentPortfolios(getLocalStorage);
    
        if ((!recentPortfolioIds || recentPortfolioIds.length === 0) && action === 'ADD') {
            recentPortfolioIds = [portfolioId];
        } else {
            const recentPortfolioIndex = recentPortfolioIds.indexOf(portfolioId);
    
            if (recentPortfolioIndex !== -1) {
                recentPortfolioIds.splice(recentPortfolioIndex, 1);
            }
    
            if (action === RecentPortfoliosActions.ADD) {
                // put the portfolio at top of the list
                recentPortfolioIds.unshift(portfolioId);
            } // else action === 'REMOVE', nothing to do
        }
    
        // use localStorageService to save to the list
        LocalStorage.setRecentPortfolios(recentPortfolioIds, setLocalStorage);
        setRecentPortfolioIds(recentPortfolioIds);
    };

    /**
     * Returns the most recent portfolio ids
     * @param count the number of portfolio ids to return
     * @returns 
     */
    const getMostRecent = (count: number): string[] => {
        let ids: string[] = LocalStorage.getRecentPortfolios(getLocalStorage);
    
        // compare recent portfolio array and update state only if different
        if (JSON.stringify(recentPortfolioIds) !== JSON.stringify(ids)) {
            setRecentPortfolioIds(ids);
        }

        ids = ids.slice(0, count);
        return ids;
    };

    /**
     * Triggers a data refresh of the recent portfolio list, 
     * called when a tco assessment was created or deleted or a portfolio name was changed
     */
    const refreshList = () => {
        const ids: string[] = LocalStorage.getRecentPortfolios(getLocalStorage);
        setRecentPortfolioIds(ids);
    };

    return (
        <RecentPortfoliosContext.Provider
            value={{recentPortfolioIds, visited, getMostRecent, refreshList}}
        >
            {children}
        </RecentPortfoliosContext.Provider>
    );
};
